import { LineSeriesOption } from "echarts/charts";
import { DataPoint } from "regression";

export interface LeadingSegmentOptions {
  ratio?: number;
  disabled?: boolean;
}

export const splitLeadingFlowSegment = (
  points: DataPoint[] = [],
  ratio: number = 0.2
) => {
  if (points.length < 2) {
    return {
      leading: points,
      trailing: points,
    };
  }

  const startX = points[0][0];
  const endX = points[points.length - 1][0];
  const thresholdX = startX + (endX - startX) * ratio;
  const leading: DataPoint[] = [];
  const trailing: DataPoint[] = [];

  for (let i = 0; i < points.length - 1; i += 1) {
    const current = points[i];
    const next = points[i + 1];
    const [x0, y0] = current;
    const [x1, y1] = next;

    if (x0 <= thresholdX) {
      leading.push(current);
    }

    if (x0 >= thresholdX) {
      trailing.push(current);
    }

    if (x0 < thresholdX && x1 > thresholdX) {
      const t = (thresholdX - x0) / (x1 - x0);
      const thresholdY = y0 + (y1 - y0) * t;
      const pivot: DataPoint = [thresholdX, thresholdY];
      leading.push(pivot);
      trailing.push(pivot);
    }
  }

  const lastPoint = points[points.length - 1];

  if (lastPoint[0] <= thresholdX) {
    leading.push(lastPoint);
  }

  if (lastPoint[0] >= thresholdX) {
    trailing.push(lastPoint);
  }

  return {
    leading: leading.length > 1 ? leading : points,
    trailing: trailing.length > 1 ? trailing : points,
  };
};

export const buildLeadingDashedSeries = (
  config: LineSeriesOption,
  points: DataPoint[] = [],
  options: LeadingSegmentOptions = {}
): LineSeriesOption[] => {
  const { ratio = 0.2, disabled = false } = options;

  if (disabled) {
    return [
      {
        ...config,
        emphasis: {
          disabled: true,
        },
        data: points,
      },
    ];
  }

  const { leading, trailing } = splitLeadingFlowSegment(points, ratio);

  return [
    {
      ...config,
      name: `${config.name}-leading`,
      label: { show: false },
      // Keep the leading guide segment as a plain dashed polyline so it does
      // not create a second independently-smoothed curve near the main line.
      smooth: false,
      emphasis: {
        disabled: true,
      },
      lineStyle: {
        ...(config.lineStyle || {}),
        type: "dashed",
      },
      data: leading,
      silent: true,
    },
    {
      ...config,
      emphasis: {
        disabled: true,
      },
      data: trailing,
    },
  ];
};
